import { JsonElementType, JsonObject, JsonProperty } from "ta-json";

import type { Nullable } from "../../base-types";
import CultureInfo from "../../culture-info";
import { CultureLoadOption } from "./culture-load-option";
import { PropertyLoadOption } from "./property-load-option";
import { IQueryLoadConfiguration, QueryLoadConfiguration } from "./query-load-configuration";
import { RelationLoadOption } from "./relation-load-option";

/**
 * Specifies if and how resulting entities should be loaded.
 */
@JsonObject()
export class EntityLoadOption {
    /**
     * Indicates if entities should be loaded.
     */
    @JsonProperty("load_entities")
    loadEntities: boolean;

    /**
     * The names of the properties to load with the entities.
     * If the value contains "*", all properties will be loaded.
     * If the value is null, no properties will be loaded.
     *
     * The default value is ["*"], which means all properties will be loaded.
     */
    @JsonProperty("properties_to_load")
    @JsonElementType(String)
    propertiesToLoad: Nullable<Array<string>> = ["*"];

    /**
     * The names of the relations to load with the entities.
     * If the value is null, no relations will be loaded.
     *
     * The default value is null, which mean no relations will be loaded.
     */
    @JsonProperty("relations_to_load")
    @JsonElementType(String)
    relationsToLoad: Nullable<Array<string>> = null;

    /**
     * The cultures to load the entities in.
     * If not specified, the entities will only be loaded in the invariant-culture.
     *
     * If the collection contains value "*", the entities will be loaded in all installed cultures.
     */
    @JsonProperty("cultures_to_load")
    @JsonElementType(String)
    culturesToLoad?: Array<CultureInfo | "*">;

    toQueryLoadConfiguration(): IQueryLoadConfiguration {
        const result = new QueryLoadConfiguration();
        result.loadEntities = this.loadEntities;

        // Map properties to load
        if (this.propertiesToLoad === null || this.propertiesToLoad.length === 0) {
            result.propertyLoadOption = PropertyLoadOption.None;
        } else if (this.propertiesToLoad.includes("*")) {
            result.propertyLoadOption = PropertyLoadOption.All;
        } else {
            result.propertyLoadOption = new PropertyLoadOption(this.propertiesToLoad);
        }

        // Map relations to load
        if (this.relationsToLoad === null || this.relationsToLoad.length === 0) {
            result.relationLoadOption = RelationLoadOption.None;
        } else {
            result.relationLoadOption = new RelationLoadOption(this.relationsToLoad);
        }

        // Map cultures to load
        if (!this.culturesToLoad || this.culturesToLoad.length === 0) {
            result.cultureLoadOption = CultureLoadOption.None;
        } else if (this.culturesToLoad.includes("*")) {
            result.cultureLoadOption = CultureLoadOption.All;
        } else {
            result.cultureLoadOption = new CultureLoadOption(this.culturesToLoad as Array<CultureInfo>);
        }

        return result;
    }
}
